import React, { useEffect, useState } from "react";
import { Building2, Mail, MapPin, Phone, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MobilePage, GlassCard } from "@/components/mobile/MobilePrimitives";

function Field({ icon: Icon, label, value }) {
  return (
    <GlassCard className="p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
          <div className="truncate text-sm font-black text-slate-900">{value || "-"}</div>
        </div>
      </div>
    </GlassCard>
  );
}

export default function MobileProfile() {
  const [emp, setEmp] = useState({});
  const [form, setForm] = useState({ phone: "", address: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/profile");
      setEmp(data || {});
      setForm({ phone: data?.phone || "", address: data?.address || "" });
    } catch {
      setEmp({});
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", form);
      toast.success("Profile updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to update profile");
    } finally {
      setSaving(false);
    }
  };

  const fullName = emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Employee";
  const initials = `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}` || fullName[0] || "U";

  return (
    <MobilePage>
      <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-400 p-5 text-white shadow-xl">
        <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-[28px] border-4 border-white/40 bg-white/20 text-3xl font-black">
            {initials}
          </div>
          <div>
            <div className="text-2xl font-black">{fullName}</div>
            <div className="text-sm text-white/85">{emp.position || "Team Member"}</div>
            <div className="mt-2 inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-bold">
              {emp.employee_no || "Employee"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Field icon={Building2} label="Department" value={emp.department || "Pending Assignment"} />
        <Field icon={Mail} label="Email" value={emp.email} />
        <Field icon={ShieldCheck} label="Status" value={emp.status || emp.employment_status || "active"} />
      </div>

      <GlassCard className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <Phone className="h-5 w-5 text-blue-700" />
          <div className="text-lg font-black">Contact Information</div>
        </div>

        <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Phone</label>
        <input
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:ring-2 focus:ring-blue-200"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />

        <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-slate-400">Address</label>
        <textarea
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:ring-2 focus:ring-blue-200"
          rows={3}
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />

        <button
          onClick={save}
          disabled={saving}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-4 font-black text-white shadow-lg disabled:opacity-60"
        >
          <Save className="h-5 w-5" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </GlassCard>

      <GlassCard className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-black">HR-controlled fields</div>
            <div className="text-xs text-slate-500">Employment, salary, and government IDs are updated by HR.</div>
          </div>
        </div>
      </GlassCard>
    </MobilePage>
  );
}
